import { Alert, Box, Button, Chip, Divider, Stack, Typography } from '@mui/material'
import { CheckCircleRounded } from '@mui/icons-material'
import SmartMateriaUITable from '../../../generic/SmartMateriaUITable'
import { SmartDialog } from '../../../generic/utilities/SmartDialog'
import { DefaultAccordion } from '../../../generic/DefaultAccordion'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { formatTemplateVersion } from './templateLinks'

/** Shared by every list of the dialog: compact, read-only, no search or pagination, Sun's order (as `OperationComparisonView`). */
const compactTableProps = {
  dense: true,
  sticky: false,
  searchAndPaginationVisible: 'none',
  tablePageSize: Number.MAX_SAFE_INTEGER,
  primaryKeys: ['id'],
  sortOptions: { disableAutoSort: true }
}

/** Sun's `null`s (no previous code, no job order, no linked template) are read here and painted as a dash. */
const shown = (value) => value === null || value === undefined || value === '' ? '—' : String(value)
const pathText = (path) => Array.isArray(path) && path.length > 0 ? path.join(' / ') : '—'

/**
 * One collapsible section of the dialog (the generic `DefaultAccordion` of the editor): its title and number in the header, also
 * when it is 0. Folded by default so the dialog reads as a summary.
 */
const ImpactAccordion = ({ title, count, defaultExpanded = false, disabled = false, testId = 'impact-section', children }) => (
  <Box data-testid={testId} data-title={title}>
    <DefaultAccordion
      defaultExpanded={defaultExpanded}
      disabled={disabled}
      contentPadding='1em'
      headerRender={
        <Stack direction='row' spacing={1} alignItems='center'>
          <Typography variant='subtitle2'>{title}</Typography>
          <Chip size='small' label={count} data-testid='impact-section-count' />
        </Stack>
      }
    >
      {children}
    </DefaultAccordion>
  </Box>
)

/** A list of the impact: its table inside its own collapsible section; an empty list is disabled, there is nothing to open. */
const ImpactSection = ({ title, columns, rows }) => (
  <ImpactAccordion title={title} count={rows.length} disabled={rows.length === 0}>
    {rows.length > 0 ? <SmartMateriaUITable {...compactTableProps} columns={columns} dataFetch={() => Promise.resolve(rows)} /> : <span />}
  </ImpactAccordion>
)

/** "Library BOM <code> v<n>" | "Embedded BOM (<n> components)" | "No BOM" (a `null` summary is no BOM, contrato-sun.md §11.5). */
const bomText = (summary, t_) => {
  if (!summary) {
    return t_('No BOM')
  }
  return summary.kind === 'LIBRARY'
    ? t_('Library BOM {{code}} v{{version}}', { code: summary.libraryCode, version: summary.libraryVersionNumber })
    : t_('Embedded BOM ({{count}} components)', { count: summary.componentCount })
}

/**
 * The BOM section (RN-03.15 point 9): what happens to the workflow BOM and how many assignments are lost (also 0, H10). Open by
 * default — it carries the warnings of a replaced or removed BOM; its number is the assignments lost.
 */
const BomSection = ({ bom, t_ }) => (
  <ImpactAccordion title={t_('BOM')} count={bom.lostAssignmentCount} defaultExpanded testId='impact-bom'>
    <Stack spacing={0.5}>
      <BomSectionContent bom={bom} t_={t_} />
    </Stack>
  </ImpactAccordion>
)

const BomSectionContent = ({ bom, t_ }) => (
  <>
    <Typography variant='body2'>
      {bom.change === 'KEPT'
        ? t_('The workflow BOM is not changed')
        : bom.change === 'NONE' ? bomText(bom.current, t_) : bomText(bom.current, t_) + ' → ' + bomText(bom.target, t_)}
    </Typography>
    {bom.change === 'REPLACED' ? <Alert severity='warning'>{t_('The workflow BOM will be replaced by the template BOM')}</Alert> : null}
    {bom.change === 'REMOVED' ? <Alert severity='warning'>{t_('The workflow will have no BOM: the template has no BOM')}</Alert> : null}
    <Typography variant='body2'>
      {t_('{{count}} component assignments of {{jobs}} jobs will be lost', { count: bom.lostAssignmentCount, jobs: bom.jobsLosingAssignmentsCount })}
    </Typography>
  </>
)

/**
 * TemplateUpdateImpactDialog — story 224 (S120-S126, S143, CU8 paso 6): what updating a unit to the newer template version
 * does, painted from E3's `TemplateUpdateImpact` (contrato-sun.md §10, §11); it computes nothing.
 *
 * Adjustment 2026-10-29: built with the generic Smart pieces — `SmartDialog` and one compact `SmartMateriaUITable` per list, as
 * `OperationUpdateImpactDialog` and `OperationComparisonView` do. Confirming saves nothing: the editor keeps the update as a pending
 * change that the window's Save writes, as a new version or in place (G5: the dialog announces neither a version nor an index).
 * `viewOnly` reopens the impact of an update already accepted ("View changes"): only Close, no Update.
 *
 * @param {object} props
 * @param {object} props.impact the `TemplateUpdateImpact`; `{}` keeps the dialog closed
 * @param {string[]} props.discardedOperationJobs the codes of the jobs of the unit with a pending library-operation intent (#130): accepting
 * replaces them, so the dialog says those changes are discarded (G2, S158); nothing is said when there are none (S161)
 * @param {boolean} props.viewOnly the update is already pending: the dialog only shows what it does
 * @param {function()} props.onCancel closes it without changing anything (S124)
 * @param {function()} props.onConfirm the user accepted the update (it becomes a pending change of the editor)
 */
const TemplateUpdateImpactDialog = ({ impact = {}, viewOnly = false, discardedOperationJobs = [], onCancel = () => {}, onConfirm = () => {} }) => {
  const { t_ } = useSmartTranslation()
  if (!impact.scope) {
    return null
  }
  const isRoot = impact.scope === 'ROOT'
  // S123: more than the local changes (which the dialog always announces) goes away: jobs or subworkflows of the workflow,
  // its BOM, or component assignments.
  const losesSomething = impact.lostOwnJobs.length > 0 || impact.lostSubworkflows.length > 0 ||
    impact.bom.change === 'REPLACED' || impact.bom.change === 'REMOVED' || impact.bom.lostAssignmentCount > 0
  const preferredMachineLost = impact.keptJobs.filter((job) => job.preferenceDeviceCleared === true)

  const jobColumns = [
    { name: t_('Path'), field: 'pathText', readOnly: true },
    { name: t_('Code'), field: 'code', readOnly: true },
    { name: t_('Job order'), field: 'orderText', readOnly: true },
    { name: t_('Before'), field: 'previousCodeText', readOnly: true }
  ]
  const jobRows = (jobs) => jobs.map((job, index) => ({
    id: job.nodeId + '#' + index,
    pathText: pathText(job.path),
    code: shown(job.code),
    orderText: shown(job.jobOrder),
    previousCodeText: shown(job.previousCode)
  }))
  const jobSection = (title, jobs) => <ImpactSection title={t_(title)} columns={jobColumns} rows={jobRows(jobs)} />

  const subworkflowColumns = [
    { name: t_('Path'), field: 'pathText', readOnly: true },
    { name: t_('Code'), field: 'code', readOnly: true },
    { name: t_('Template'), field: 'templateText', readOnly: true },
    { name: t_('Jobs'), field: 'jobCount', readOnly: true }
  ]
  const subworkflowRows = impact.lostSubworkflows.map((subworkflow, index) => ({
    id: subworkflow.nodeId + '#' + index,
    pathText: pathText(subworkflow.path),
    code: shown(subworkflow.code),
    templateText: subworkflow.linkedTemplateCode
      ? subworkflow.linkedTemplateCode + ' v' + formatTemplateVersion(subworkflow.linkedTemplateVersion)
      : '—',
    jobCount: shown(subworkflow.jobCount)
  }))

  return (
    <SmartDialog
      setOpen
      title={{ render: <Typography variant='h6'>{t_('Update from template')}</Typography> }}
      cancel={!viewOnly}
      cancelLabel='Cancel'
      cancelCallback={onCancel}
      close={viewOnly}
      closeCallback={onCancel}
      // The generic accept button is always `primary`; S123 needs `warning` when something more than local changes is lost, so the
      // Update button is a custom one with the same look (as `SmartDeleteDialog` does with its DELETE).
      accept={false}
      renderCustomButtons={viewOnly
        ? undefined
        : (
          <Button variant='outlined' color={losesSomething ? 'warning' : 'primary'} onClick={onConfirm} data-testid='template-update-confirm'>
            <Typography>{t_('Update')}</Typography>
            <CheckCircleRounded color={losesSomething ? 'warning' : 'primary'} sx={{ fontSize: '1.4em', marginLeft: '0.3rem' }} />
          </Button>
          )}
      autoClose={false}
      paperWidth='60rem'
      renderComponent={
        <Stack spacing={2} sx={{ textAlign: 'left' }} data-testid='template-update-impact-dialog'>
          <Stack spacing={0.5}>
            <Typography data-testid='impact-unit'>
              {(isRoot ? t_('Whole workflow') : impact.nodePath.join(' / ')) + ' · ' + t_('Template {{code}}: v{{from}} → v{{to}}', {
                code: impact.templateCode,
                from: formatTemplateVersion(impact.fromTemplateVersion),
                to: formatTemplateVersion(impact.toTemplateVersion)
              })}
            </Typography>
            <Typography variant='body2' color='text.secondary' data-testid='impact-save-mode'>
              {t_('The update will be applied when you save, as a new version or on this version')}
            </Typography>
          </Stack>
          {!viewOnly && discardedOperationJobs.length > 0
            ? (
              <Alert severity='warning' data-testid='impact-discarded-operations'>
                <Typography variant='body2'>{t_('Pending library operation changes on these jobs will be discarded')}</Typography>
                <Typography variant='body2' data-testid='impact-discarded-operation-jobs'>{discardedOperationJobs.join(', ')}</Typography>
              </Alert>
              )
            : null}
          {viewOnly ? <Alert severity='warning' data-testid='impact-pending'>{t_('Template update pending: save the workflow to apply it')}</Alert> : null}
          <Divider />
          {jobSection('Jobs that keep their balance', impact.keptJobs)}
          {jobSection('Jobs with changed data', impact.changedJobs)}
          {jobSection('Jobs that will be removed', impact.removedJobs)}
          {jobSection('New jobs without balance', impact.newJobs)}
          {jobSection('Jobs that lose their preferred machine', preferredMachineLost)}
          {jobSection('Jobs added to this workflow that will be lost', impact.lostOwnJobs)}
          <ImpactSection title={t_('Subworkflows that will be lost')} columns={subworkflowColumns} rows={subworkflowRows} />
          <Divider />
          {isRoot ? <Alert severity='warning'>{t_('The whole workflow will become the new template version: jobs and subworkflows that do not come from the template will be lost, and the workflow documents will be replaced by the template documents.')}</Alert> : null}
          <BomSection bom={impact.bom} t_={t_} />
          <Alert severity='info'>{t_('Local changes to these jobs will be lost; only the balance is kept.')}</Alert>
        </Stack>
      }
    />
  )
}

export default TemplateUpdateImpactDialog
